import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { APP_STORE_URL, PLAY_STORE_URL } from "../AppDeepLink";
import "./tests.scss";

const fallbackData = {
  backToTests: "Тест рүү буцах",
  questionProgress: "Асуулт {current}/{total}",
  chooseAnswer: "Үргэлжлүүлэхийн өмнө хариултаа сонгоно уу.",
  answerAllQuestions: "Илгээхийн өмнө бүх асуултад хариулна уу.",
  previous: "Өмнөх",
  next: "Дараах",
  submit: "Илгээх",
  placementLevel: "CEFR ойролцоо түвшин",
  scoreSummary: "Та {total} асуултаас {score}-д зөв хариуллаа.",
  scoreBreakdown: "Түвшин бүрийн оноо",
  recommendedNextStep: "Дараагийн алхам",
  courseRecommendation: "Tomko апп дээр англи хэлийг ABC-ээс IELTS хүртэл сурах хичээлүүд бий. Апп татаад {level} түвшний хичээлээр үргэлжлүүлээрэй.",
  downloadOnAppStore: "App Store-оос татах",
  getItOnGooglePlay: "Google Play-ээс татах",
  resultDisclaimer: "Энэ нь олон сонголттой асуултад үндэслэсэн хурдан түвшин тогтоох үнэлгээ юм. Ярих, бичих чадвар эцсийн түвшинг өөрчилж болно.",
  retake: "Дахин өгөх",
  items: {},
};

const DEFAULT_PASS_THRESHOLD_PERCENT = 80;

const formatText = (template, values) => {
  return Object.entries(values).reduce(
    (text, [key, value]) => text.replace(`{${key}}`, value),
    template
  );
};

const getLocalizedValue = (value, language) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return value;
  return value[language] || value.en || value.mn || "";
};

const getLevelScore = (level, levelQuestions, answers) => {
  const correct = levelQuestions.filter((question) => answers[question.id] === question.correctAnswer).length;

  return {
    ...level,
    correct,
    total: levelQuestions.length,
    percent: levelQuestions.length > 0
      ? Math.round((correct / levelQuestions.length) * 100)
      : 0,
  };
};

const EnglishPlacementTest = ({ test, data = fallbackData, language = "en" }) => {
  const text = { ...fallbackData, ...data };
  const testText = {
    ...test,
    ...(text.items?.[test.id] || {}),
  };
  const questions = useMemo(() => {
    return test.sections.flatMap((section) => section.questions || []);
  }, [test.sections]);
  const questionsByLevel = useMemo(() => {
    return test.levels.reduce((groupedQuestions, level) => ({
      ...groupedQuestions,
      [level.code]: questions.filter((question) => question.level === level.code),
    }), {});
  }, [questions, test.levels]);
  const [answers, setAnswers] = useState({});
  const [currentLevelIndex, setCurrentLevelIndex] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const passThresholdPercent = test.passThresholdPercent || DEFAULT_PASS_THRESHOLD_PERCENT;
  const currentLevel = test.levels[currentLevelIndex];
  const currentLevelQuestions = questionsByLevel[currentLevel?.code] || [];
  const currentQuestion = currentLevelQuestions[currentQuestionIndex];
  const isLastQuestionInLevel = currentQuestionIndex === currentLevelQuestions.length - 1;
  const isLastLevel = currentLevelIndex === test.levels.length - 1;

  const handleAnswerChange = (questionId, value) => {
    setAnswers((currentAnswers) => ({
      ...currentAnswers,
      [questionId]: value,
    }));
    setError("");
    setResults(null);
  };

  const calculateResults = (resultLevelIndex) => {
    const attemptedLevels = test.levels.slice(0, resultLevelIndex + 1);
    const levelScores = attemptedLevels.map((level) => (
      getLevelScore(level, questionsByLevel[level.code] || [], answers)
    ));
    const score = levelScores.reduce((totalScore, levelScore) => totalScore + levelScore.correct, 0);
    const total = levelScores.reduce((totalQuestions, levelScore) => totalQuestions + levelScore.total, 0);
    const level = attemptedLevels[attemptedLevels.length - 1] || test.levels[0];

    return {
      score,
      total,
      percent: total > 0 ? Math.round((score / total) * 100) : 0,
      level,
      levelScores,
    };
  };

  const goToPreviousQuestion = () => {
    setCurrentQuestionIndex((index) => Math.max(index - 1, 0));
    setError("");
  };

  const completeCurrentLevel = () => {
    const levelScore = getLevelScore(currentLevel, currentLevelQuestions, answers);

    if (levelScore.percent < passThresholdPercent || isLastLevel) {
      setResults(calculateResults(currentLevelIndex));
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setCurrentLevelIndex((index) => Math.min(index + 1, test.levels.length - 1));
    setCurrentQuestionIndex(0);
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goToNextQuestion = () => {
    if (!currentQuestion || answers[currentQuestion.id] === undefined) {
      setError(text.chooseAnswer);
      return;
    }

    if (isLastQuestionInLevel) {
      completeCurrentLevel();
      return;
    }

    setCurrentQuestionIndex((index) => Math.min(index + 1, currentLevelQuestions.length - 1));
    setError("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleRetake = () => {
    setAnswers({});
    setResults(null);
    setError("");
    setCurrentLevelIndex(0);
    setCurrentQuestionIndex(0);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderHeader = () => (
    <section className="upper-section">
      <div className="upper-section-inner">
        <div className="title-container">
          <h2>{getLocalizedValue(testText.title, language)}</h2>
        </div>
      </div>
    </section>
  );

  if (results) {
    return (
      <div className="items-container" id="tests">
        {renderHeader()}

        <main className="main-container test-runner">
          <Link to="/tests" className="test-runner__back">{text.backToTests}</Link>
          <section className="english-placement-result" aria-live="polite">
            <div className="english-placement-result__summary">
              <span>{text.placementLevel}</span>
              <h3>{results.level.code}</h3>
              <p>{getLocalizedValue(results.level.name, language)}</p>
              <strong>{formatText(text.scoreSummary, { score: results.score, total: results.total })}</strong>
            </div>

            <div className="english-placement-result__details">
              <div>
                <h4>{text.recommendedNextStep}</h4>
                <p>{getLocalizedValue(results.level.description, language)}</p>
                <p>{formatText(text.courseRecommendation, { level: results.level.code })}</p>
                <p>{getLocalizedValue(results.level.nextStep, language)}</p>
                <div className="english-placement-result__app-buttons">
                  <a
                    href={PLAY_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="english-placement-result__app-btn"
                    aria-label={text.getItOnGooglePlay}
                  >
                    <img
                      src="/images/android.png"
                      alt={text.getItOnGooglePlay}
                      width={160}
                      height={53}
                    />
                  </a>
                  <a
                    href={APP_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="english-placement-result__app-btn"
                    aria-label={text.downloadOnAppStore}
                  >
                    <img
                      src="/images/ios.png"
                      alt={text.downloadOnAppStore}
                      width={160}
                      height={53}
                    />
                  </a>
                </div>
              </div>

              <div className="english-placement-breakdown">
                <h4>{text.scoreBreakdown}</h4>
                {results.levelScores.map((levelScore) => (
                  <div key={levelScore.code} className="english-placement-breakdown__row">
                    <span>{levelScore.code}</span>
                    <div className="english-placement-breakdown__track" aria-hidden="true">
                      <div
                        className="english-placement-breakdown__bar"
                        style={{ width: `${(levelScore.correct / Math.max(levelScore.total, 1)) * 100}%` }}
                      />
                    </div>
                    <strong>{levelScore.correct}/{levelScore.total}</strong>
                  </div>
                ))}
              </div>
            </div>

            <p className="english-placement-result__note">{text.resultDisclaimer}</p>
            <button type="button" className="test-runner__submit" onClick={handleRetake}>{text.retake}</button>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="items-container" id="tests">
      {renderHeader()}

      <main className="main-container test-runner">
        <Link to="/tests" className="test-runner__back">{text.backToTests}</Link>
        <p className="test-runner__instructions">{getLocalizedValue(test.instructions, language)}</p>

        <section className="test-section test-section--single english-placement-question-card">
          <div className="test-section__header">
            <h3>{formatText(text.questionProgress, { current: currentQuestionIndex + 1, total: currentLevelQuestions.length })}</h3>
            <span>{currentLevel?.code}</span>
          </div>
          <div className="test-progress" aria-hidden="true">
            <div
              className="test-progress__bar"
              style={{ width: `${((currentQuestionIndex + 1) / Math.max(currentLevelQuestions.length, 1)) * 100}%` }}
            />
          </div>

          {currentQuestion ? (
            <fieldset className="test-question">
              <legend>{currentQuestion.prompt}</legend>
              <div className="test-question__options english-placement-options">
                {currentQuestion.options.map((option) => (
                  <label
                    key={option.value}
                    className={answers[currentQuestion.id] === option.value ? "test-question__option--selected" : ""}
                  >
                    <input
                      type="radio"
                      name={currentQuestion.id}
                      value={option.value}
                      checked={answers[currentQuestion.id] === option.value}
                      onChange={(event) => handleAnswerChange(currentQuestion.id, event.target.value)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          ) : null}
        </section>

        {error ? <p className="test-runner__error">{error}</p> : null}
        <div className="test-runner__nav">
          <button
            type="button"
            className="test-runner__secondary"
            onClick={goToPreviousQuestion}
            disabled={currentQuestionIndex === 0}
          >
            {text.previous}
          </button>
          {isLastQuestionInLevel && isLastLevel ? (
            <button type="button" className="test-runner__submit" onClick={goToNextQuestion}>{text.submit}</button>
          ) : (
            <button type="button" className="test-runner__submit" onClick={goToNextQuestion}>{text.next}</button>
          )}
        </div>
      </main>
    </div>
  );
};

export default EnglishPlacementTest;
